import theme from '@/common/theme';
import Alert from '@/components/alert/alert-component';
import FileUpload from '@/components/file-upload/file-upload-component';
import { Col, Row } from '@/components/grid/grid-component';
import Progress from '@/components/progress-bar/progress-bar-component';
import Input from '@/components/react-hook-form-inputs/input/input-component';
import StepperComponent from '@/components/stepper/stepper-component';
import { createTemplateExcel } from '@/utils/excel-utils';
import { Subtitle2Text } from '@/utils/global-fonts';
import { S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FiPercent } from 'react-icons/fi';
import Checkbox from '../react-hook-form-inputs/checkbox/checkbox-component';

const modalRowStyle = {
  marginLeft: '0px',
  marginRight: '0px',
  marginBottom: '0px',
};

const modalColStyle = {
  paddingLeft: '0px',
  paddingRight: '0px',
};

const styledCol = {
  paddingLeft: '0px',
};

const checkboxColStyle = {
  paddingLeft: '0px',
  paddingRight: '0px',
  marginBottom: '10px',
};

const Step1 = ({ setFilesAccepted, progress }) => {
  const [fileName, setFileName] = useState('');
  const [isDropped, setIsDropped] = useState(false);

  const onDrop = (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      Alert({
        icon: 'error',
        text: 'Please upload only XLSX files.',
      });
      setIsDropped(false);
      setFileName('');
    } else {
      acceptedFiles.map((file) => setFileName(file.name));
      setFilesAccepted(acceptedFiles);

      setIsDropped(true);
    }
  };

  const { watch } = useFormContext();
  const removeMissingPartNumbers = watch('remove_missing_part_numbers');

  return (
    <Row style={modalRowStyle}>
      {progress === 0 || progress >= 100 ? (
        <Col style={modalColStyle} xs={12}>
          <FileUpload
            fileType="xlsx"
            onDrop={onDrop}
            fileName={fileName}
            isDropped={isDropped}
          />
        </Col>
      ) : (
        <Col style={modalColStyle} xs={12}>
          <Progress
            fileName={fileName}
            progress={progress}
            color={theme.COLOR_PRIMARY}
          />
        </Col>
      )}

      <Col style={checkboxColStyle} xs={12}>
        <Checkbox
          label="Remove missing part numbers"
          name="remove_missing_part_numbers"
          registerOptions={{
            required: false,
          }}
        />
      </Col>

      {removeMissingPartNumbers && (
        <Col style={checkboxColStyle} xs={12}>
          <Checkbox
            label="Remove temporary part numbers"
            name="remove_temporary_part_numbers"
            registerOptions={{
              required: false,
            }}
          />
        </Col>
      )}
    </Row>
  );
};
Step1.defaultProps = {
  setFilesAccepted: () => {},
  progress: 0,
};

Step1.propTypes = {
  setFilesAccepted: PropTypes.func,
  progress: PropTypes.number,
};
const Step2 = () => (
  <>
    <Row>
      <Col xs={12}>
        <Subtitle2Text>End User Gross profit</Subtitle2Text>
      </Col>
    </Row>
    <Row style={modalRowStyle}>
      <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
        <Input
          disabled={false}
          label="Gross Profit %"
          placeholder="%"
          type="number"
          name="end_user_gross_profit"
          iconRight={<FiPercent />}
          registerOptions={{
            required: false,
            valueAsNumber: true,
            max: 99.99,
            min: 0,
          }}
        />
      </Col>
    </Row>
    <Row>
      <Col xs={12}>
        <Subtitle2Text>Transfer Price Gross profit</Subtitle2Text>
      </Col>
    </Row>
    <Row style={modalRowStyle}>
      <Col style={styledCol} sm={12} md={12} lg={12} xl={12} xxl={12}>
        <Input
          disabled={false}
          label="Gross Profit %"
          placeholder="%"
          type="number"
          iconRight={<FiPercent />}
          name="transfer_price_gross_profit"
          registerOptions={{
            required: false,
            valueAsNumber: true,
            max: 99.99,
            min: 0,
          }}
        />
      </Col>
    </Row>
  </>
);

/**
 * Create a pricing modal
 * @param {object} root0 - The root object containing props
 * @param {Function} root0.onClose - Callback function to close the modal
 * @param {string} root0.companyId - CompanyId that is either from a selected Supplier or Company itself
 * @returns {JSX.Element} - The rendered JSX element
 */
const CreatePricingModal = ({ companyId, onClose }) => {
  const [progress, setProgress] = useState(0);
  const [filesAccepted, setFilesAccepted] = useState([]);
  const [next, setNext] = useState(false);
  const { setValue } = useFormContext();
  const uploadToS3 = async (fileToUpload) => {
    const file = fileToUpload[0];
    const target = {
      Bucket: import.meta.env.VITE_BUCKET_NAME,
      Key: `pricing-upload/${file.name}`,
      Body: file,
    };

    let parts = {};

    if (file.size >= 1024 * 1024 * 2) {
      parts = {
        queueSize: 4, // optional concurrency configuration
        partSize: 1024 * 1024 * 2, // optional size of each part, in bytes, at least 5MB
      };
    }

    try {
      const parallelUploads3 = new Upload({
        client: new S3Client({
          region: import.meta.env.VITE_REGION ?? 'eu-west-1',
          credentials: {
            accessKeyId: import.meta.env.VITE_BUCKET_ACCESS_KEY,
            secretAccessKey: import.meta.env.VITE_BUCKET_SECRET_KEY,
          },
        }),
        params: target,

        tags: [
          /* ... */
        ], // optional tags
        ...parts,
        leavePartsOnError: false, // optional manually handle dropped parts
      });

      parallelUploads3.on('httpUploadProgress', (prog) => {
        if (prog.total === prog.loaded) {
          setProgress(100);
        } else {
          setProgress(Math.round((prog.loaded / prog.total) * 100));
        }
      });

      await parallelUploads3.done().then(() => {
        setValue('file_name', file.name);
        setNext(true);
      });
    } catch (e) {
      Alert({
        icon: 'error',
        text: e.message,
      });
    }
  };
  const steps = [
    <Step1
      name="Import File"
      key="step1"
      primaryButtonTitle="Import"
      onSecondaryClick={() => {
        createTemplateExcel(
          ['part_number', 'name', 'cost_price', 'currency_name'],
          'Bulk add pricing',
        );
      }}
      nextStep={next}
      setFilesAccepted={setFilesAccepted}
      progress={progress}
      onPrimaryClick={() => {
        if (companyId === undefined || companyId === '' || companyId === null) {
          Alert({
            icon: 'error',
            text: 'Please select a supplier',
          });
          onClose();
        }
        if (filesAccepted.length === 0) {
          Alert({
            icon: 'error',
            text: 'No files have been selected for upload.',
          });
        } else {
          uploadToS3(filesAccepted);
        }
      }}
      secondaryButtonTitle="Download Template"
      onClose={onClose}
    />,
    <Step2
      name="Apply Gross Profit"
      key="step2"
      primaryButtonTitle="Update"
      onSecondaryClick={() => {}}
      onPrimaryClick={() => {}}
      secondaryButtonTitle="Skip"
    />,
  ];

  return (
    <Row style={modalRowStyle}>
      <StepperComponent steps={steps} />
    </Row>
  );
};

CreatePricingModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  companyId: PropTypes.string.isRequired,
};

export default CreatePricingModal;
